import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { Save as SaveIcon, Undo as UndoIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { LessonCard } from '../../lib/components/LessonCard';
import { PeriodSlot } from '../../lib/components/PeriodSlot';
import { useTimetableStore } from '../../stores/timetableStore';
import type { DayOfWeek, Lesson } from '../../types';

const DAYS: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const PERIODS = [1, 2, 3, 4, 5];

const DAY_LABELS: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};

const EditTimetable: React.FC = () => {
  const {
    currentTimetable,
    moveLessonToSlot,
    validateLessonMove,
    getSlotConflicts,
    filterByYearGroup,
  } = useTimetableStore();

  const [selectedYearGroup, setSelectedYearGroup] = useState<string>('year-7');
  const [activeLesson, setActiveLesson] = useState<Lesson | null>(null);
  const [saveMessage, setSaveMessage] = useState<string>('');

  if (!currentTimetable) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="warning">No timetable available for editing. Please generate a timetable first.</Alert>
      </Container>
    );
  }

  const filteredLessons = filterByYearGroup(selectedYearGroup);

  const getLessonsForSlot = (day: DayOfWeek, period: number): Lesson[] => {
    return filteredLessons.filter((l) => l.day === day && l.period === period);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const lesson = filteredLessons.find((l) => l.id === event.active.id);
    setActiveLesson(lesson || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveLesson(null);

    if (!over) return;

    const lessonId = active.id as string;
    const [day, periodStr] = (over.id as string).split('-');
    const period = Number.parseInt(periodStr);

    // Validate the move
    const validation = validateLessonMove(lessonId, day as DayOfWeek, period);

    if (!validation.valid) {
      // Show conflict warning
      alert(`Cannot move lesson:\n${validation.conflicts.join('\n')}`);
      return;
    }

    // Execute the move
    moveLessonToSlot(lessonId, day as DayOfWeek, period);
    setSaveMessage('Lesson moved successfully. Remember to save changes.');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleSave = () => {
    setSaveMessage('Changes saved successfully!');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleUndo = () => {
    // Reload the timetable from the store (resets changes)
    window.location.reload();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1">
            Edit Timetable
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentTimetable.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<UndoIcon />} onClick={handleUndo}>
            Undo Changes
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
            Save Timetable
          </Button>
        </Box>
      </Box>

      {saveMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {saveMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Year Group</InputLabel>
              <Select
                value={selectedYearGroup}
                label="Year Group"
                onChange={(e) => setSelectedYearGroup(e.target.value)}
              >
                <MenuItem value="year-7">Year 7</MenuItem>
                <MenuItem value="year-8">Year 8</MenuItem>
                <MenuItem value="year-9">Year 9</MenuItem>
                <MenuItem value="year-10">Year 10</MenuItem>
                <MenuItem value="year-11">Year 11</MenuItem>
                <MenuItem value="year-12">Year 12</MenuItem>
                <MenuItem value="year-13">Year 13</MenuItem>
              </Select>
            </FormControl>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Period</TableCell>
                    {DAYS.map((day) => (
                      <TableCell key={day} align="center" sx={{ fontWeight: 'bold' }}>
                        {DAY_LABELS[day]}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {PERIODS.map((period) => (
                    <TableRow key={period}>
                      <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top' }}>Period {period}</TableCell>
                      {DAYS.map((day) => {
                        const lessons = getLessonsForSlot(day, period);
                        const conflicts = getSlotConflicts(day, period);
                        return (
                          <TableCell key={`${day}-${period}`} sx={{ p: 1, verticalAlign: 'top' }}>
                            <PeriodSlot day={day} period={period} lessons={lessons} conflicts={conflicts} />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <DragOverlay>{activeLesson ? <LessonCard lesson={activeLesson} showDragHandle={false} /> : null}</DragOverlay>
          </DndContext>
        </Grid>

        <Grid item xs={12}>
          <Alert severity="info">
            <Typography variant="body2" fontWeight="bold">
              Instructions:
            </Typography>
            <Typography variant="caption" component="div">
              • Drag lessons to different time slots to reorganize the timetable
            </Typography>
            <Typography variant="caption" component="div">
              • The system will prevent moves that create conflicts (teacher/room/group double-booking)
            </Typography>
            <Typography variant="caption" component="div">
              • Red borders indicate conflicts in a time slot
            </Typography>
            <Typography variant="caption" component="div">
              • Remember to save your changes before leaving the page
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Container>
  );
};

export default EditTimetable;
